// The list's behaviour, once — so the table and the cards cannot disagree.
//
// Both surfaces render the same rows, in the same order, filtered the same way,
// with the same actions and the same toasts. What they do NOT share is chrome:
// a `DataTable` with a hover action column and a stack of cards with a FAB are
// genuinely different designs for the same screen, and forcing one component to
// be both is how a table ends up with a 44px-tall row on a desktop.
//
// So the split is: this file decides WHAT, the surfaces decide WHAT IT LOOKS
// LIKE. A behaviour change lands in one place.

import React from 'react';
import { useToast } from '@parsina/framework';

import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useCases, filterCases, sortCases } from '../../hooks/useCases.js';
import { toCreateInput, emptyCaseForm, titleProblem } from '../../lib/caseForm.js';
import { errorMessageKey } from '../../lib/bffClient.js';
import { DEFAULT_PAGE_SIZE, clampPage, offsetFor, pageCount } from '../../lib/paging.js';
import {
  DEFAULT_STATUSES, DEFAULT_SORT, toggleStatus, isFilteredEmpty, isTrulyEmpty,
} from './Patients.shared.js';

export function usePatientsScreen() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useAppNavigate();

  const [statuses, setStatuses] = React.useState(DEFAULT_STATUSES);
  const [query, setQuery] = React.useState('');
  const [sort, setSort] = React.useState(DEFAULT_SORT);

  const {
    cases, loading, error, reload, createCase, setCaseStatus, deleteCase, capped,
    hasAny, anyLoading,
  } = useCases({ statuses });

  // Filter THEN sort: sorting the whole page and slicing the matches would
  // reorder nothing the student can see, and cost the comparison on rows that
  // are not on screen.
  const visible = React.useMemo(
    () => sortCases(filterCases(cases, query), sort),
    [cases, query, sort],
  );

  // ── Client-side paging of the ≤100 rows the server returned ─────────────
  //
  // core.md §7.3: the list query is capped at 100 and the UI pages what it
  // holds through lib/paging.js. The desktop shows one page of 25 with the kit
  // `Pager`; the phone GROWS the list through «نمایش بیشتر» instead (a 34px
  // pager row is under the 44px touch floor). Both are the same arithmetic on
  // the same `visible` array, so a row is never on both page 1 and page 2.
  //
  // Any change to what `visible` IS (a search, a chip, a sort) resets to the
  // first page: page 3 of a list that now has one page is an empty screen with
  // a pager under it.
  const [page, setPageRaw] = React.useState(1);
  const [shownCount, setShownCount] = React.useState(DEFAULT_PAGE_SIZE);
  React.useEffect(() => {
    setPageRaw(1);
    setShownCount(DEFAULT_PAGE_SIZE);
  }, [query, sort, statuses]);

  const pages = pageCount(visible.length, DEFAULT_PAGE_SIZE);
  const currentPage = clampPage(page, visible.length, DEFAULT_PAGE_SIZE);
  const setPage = React.useCallback(
    (next) => setPageRaw(clampPage(next, visible.length, DEFAULT_PAGE_SIZE)),
    [visible.length],
  );
  const pageRows = React.useMemo(() => {
    const start = offsetFor(currentPage, DEFAULT_PAGE_SIZE);
    return visible.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [visible, currentPage]);

  const shownRows = React.useMemo(() => visible.slice(0, shownCount), [visible, shownCount]);
  const showMore = React.useCallback(
    () => setShownCount((n) => Math.min(n + DEFAULT_PAGE_SIZE, visible.length)),
    [visible.length],
  );

  // ── The dialogs ─────────────────────────────────────────────────────────
  const [creating, setCreating] = React.useState(false);
  const [form, setForm] = React.useState(() => emptyCaseForm());
  const [saving, setSaving] = React.useState(false);
  const [showTitleError, setShowTitleError] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const openCreate = React.useCallback(() => {
    setForm(emptyCaseForm());
    setShowTitleError(false);
    setCreating(true);
  }, []);

  const closeCreate = React.useCallback(() => setCreating(false), []);

  /**
   * Create, then go straight to the شرح حال tab.
   *
   * A patient with no history is a patient the assistant can say nothing about,
   * so the create flow does not end on the list — it ends in the editor, with
   * the hint that says the two ways to fill it (core.md §7.3).
   */
  const submitCreate = React.useCallback(async () => {
    if (titleProblem(form)) {
      setShowTitleError(true);
      return;
    }
    setSaving(true);
    try {
      const row = await createCase(toCreateInput(form));
      setCreating(false);
      if (row?.id) {
        toast.success(t('patients.created'));
        navigate('patient', { caseId: row.id, tab: 'history', hint: 'created' });
      }
    } catch (err) {
      toast.error(t(errorMessageKey(err)));
    } finally {
      setSaving(false);
    }
  }, [form, createCase, navigate, t, toast]);

  const confirmDelete = React.useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCase(deleteTarget.id);
      toast.success(t('patients.deleted'));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(t(errorMessageKey(err)));
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleteCase, t, toast]);

  const archive = React.useCallback(async (row) => {
    const next = row.status === 'archived' ? 'active' : 'archived';
    try {
      await setCaseStatus(row.id, next);
      toast.success(t(next === 'archived' ? 'patients.archived' : 'patients.unarchived'));
    } catch (err) {
      toast.error(t(errorMessageKey(err)));
    }
  }, [setCaseStatus, t, toast]);

  // ── Navigation ──────────────────────────────────────────────────────────

  const open = React.useCallback((row) => navigate('patient', { caseId: row.id }), [navigate]);

  /**
   * «شروع ویزیت» from the list opens the RECORD with the start dialog raised,
   * rather than starting a visit here.
   *
   * The reason («علت مراجعه») is a sentence the student writes, pre-filled from
   * the chief complaint, and a list row is not a place to write one. Passing it
   * through router state keeps the URL clean: `/patients/:id?start=1` would be a
   * link that re-raises the dialog every time it is pasted or refreshed.
   */
  const startVisit = React.useCallback((row) => {
    if (row.openEncounterId) {
      navigate('visit', { caseId: row.id, encounterId: row.openEncounterId });
      return;
    }
    navigate('patient', { caseId: row.id, startVisit: true });
  }, [navigate]);

  const chat = React.useCallback((row) => navigate('chat', { caseId: row.id }), [navigate]);

  // «پاک کردن فیلتر» shows EVERYTHING — `[]` is «همه» — not the default chips:
  // the copy beside it promises «برای دیدن همهٔ بیماران», and a student whose
  // only patients are archived would otherwise clear into another empty page
  // with the same button under it.
  const clearFilters = React.useCallback(() => {
    setQuery('');
    setStatuses([]);
  }, []);

  // The three verdicts a loaded list can reach, never two at once. `loading`
  // folds in the "any patients at all?" probe so neither empty state renders
  // on a question that has not been answered.
  const settled = !loading && !anyLoading;
  const trulyEmpty = settled && !error && isTrulyEmpty(hasAny);
  const filteredEmpty = settled && !error && isFilteredEmpty(hasAny, visible.length);

  return {
    // data
    cases,
    visible,
    loading: loading || (anyLoading && !error),
    error,
    capped,
    reload,
    // paging — desktop pages, mobile grows
    page: currentPage,
    pages,
    setPage,
    pageRows,
    shownRows,
    canShowMore: shownRows.length < visible.length,
    showMore,
    // toolbar
    query,
    setQuery,
    statuses,
    toggleStatusChip: (id) => setStatuses((prev) => toggleStatus(prev, id)),
    showAll: () => setStatuses([]),
    sort,
    setSort,
    clearFilters,
    trulyEmpty,
    filteredEmpty,
    // create
    creating,
    openCreate,
    closeCreate,
    form,
    setForm: (patch) => setForm((prev) => ({ ...prev, ...patch })),
    saving,
    showTitleError,
    submitCreate,
    // row actions
    open,
    startVisit,
    chat,
    archive,
    deleteTarget,
    setDeleteTarget,
    deleting,
    confirmDelete,
  };
}

export default usePatientsScreen;
